import { createContext, useContext } from "@bestowinc/tinkertoy-shell-deps/react";
import type { ReactNode } from "@bestowinc/tinkertoy-shell-deps/react";

import type { ViewLoader } from "./types";

/**
 * React context carrying the shell's {@link ViewLoader}.
 *
 * The sibling of `ModuleLoaderContext`: lets the catch-all route element
 * (`TinkertoyShell`) import the matched view's bundle without prop-drilling the
 * loader through React Router (which builds the router synchronously). Defaults
 * to `null` so consumers can detect a missing provider.
 */
const ViewLoaderContext = createContext<ViewLoader | null>(null);

export interface ViewLoaderProviderProps {
  loader: ViewLoader;
  children: ReactNode;
}

/**
 * Provides a {@link ViewLoader} to descendants via {@link ViewLoaderContext}.
 * Rendered once by `InternalTinkertoyProvider`; not a public export of this
 * package.
 */
export function ViewLoaderProvider({
  loader,
  children,
}: ViewLoaderProviderProps) {
  return (
    <ViewLoaderContext.Provider value={loader}>
      {children}
    </ViewLoaderContext.Provider>
  );
}

/**
 * Read the shell's {@link ViewLoader} from context.
 *
 * @throws {Error} if called outside the Tinkertoy shell's provider tree
 * (i.e. outside the tree set up by `ShellRoot` / `InternalTinkertoyProvider`).
 */
export function useViewLoader(): ViewLoader {
  const loader = useContext(ViewLoaderContext);
  if (loader === null) {
    throw new Error(
      "useViewLoader() must be called inside the Tinkertoy shell. " +
        "Ensure the component is rendered within the tree created by ShellRoot " +
        "(or wrapped in InternalTinkertoyProvider with a viewLoader in tests).",
    );
  }
  return loader;
}
